import { useEffect, useState } from "react";
import { volApi, formatVolError } from "./volApi";
import { Clock, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  pending_review: { label: "Pending review", color: "bg-[#FEF3C7] text-[#92400E]" },
  approved: { label: "Approved", color: "bg-[#E8ECE7] text-[#166534]" },
  rejected: { label: "Rejected", color: "bg-[#FEE2E2] text-[#991B1B]" },
};

export default function VolunteerHours() {
  const [data, setData] = useState({ rows: [], total_approved: 0, total_pending: 0 });
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      volApi.get("/volunteers/me/hours").catch(() => ({ data: { rows: [], total_approved: 0, total_pending: 0 } })),
      // Past events for the dropdown — re-using upcoming endpoint isn't ideal but
      // for MVP we let volunteers log against either an explicit past event id (free text)
      // or a free-form label. So we just expose the user's previous logs as suggestions.
      volApi.get("/volunteers/me/upcoming-events").catch(() => ({ data: [] })),
    ])
      .then(([h, e]) => {
        setData(h.data || {});
        setPastEvents(e.data || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="volunteer-hours-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="hw-eyebrow mb-2">My hours</div>
          <h1 className="font-display text-3xl font-semibold text-[#0F172A]">Log your service time</h1>
          <p className="text-[#334155] mt-2 max-w-xl">
            Submit hours for events you helped with. A HarborWell admin will approve them
            before they count toward your total.
          </p>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            data-testid="volunteer-hours-log-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554]"
          >
            <Plus className="w-4 h-4" /> Log new hours
          </button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5">
          <div className="hw-eyebrow text-[#1E3A8A] mb-1">Total approved</div>
          <div className="text-3xl font-display font-semibold text-[#0F172A]" data-testid="volunteer-hours-total-approved">
            {data.total_approved?.toFixed(2) || "0.00"}h
          </div>
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5">
          <div className="hw-eyebrow text-[#92400E] mb-1">Pending review</div>
          <div className="text-3xl font-display font-semibold text-[#0F172A]" data-testid="volunteer-hours-total-pending">
            {data.total_pending?.toFixed(2) || "0.00"}h
          </div>
        </div>
      </div>

      {showForm && (
        <LogHoursForm
          events={pastEvents}
          onDone={() => { setShowForm(false); load(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {loading ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl h-40 animate-pulse" />
      ) : data.rows.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center" data-testid="volunteer-hours-empty">
          <Clock className="w-12 h-12 mx-auto text-[#1E3A8A] opacity-50 mb-3" strokeWidth={1.5} />
          <p className="text-[#334155]">No hours logged yet. Click &quot;Log new hours&quot; above to get started.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl divide-y divide-[#F0EFEB]" data-testid="volunteer-hours-list">
          {data.rows.map((r) => {
            const badge = STATUS_BADGE[r.status] || STATUS_BADGE.pending_review;
            return (
              <div key={r.id} className="px-5 py-4 flex items-start justify-between gap-3" data-testid={`volunteer-hours-row-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#0F172A]">
                      {r.event_title || r.activity_label || "Volunteer activity"}
                    </span>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${badge.color}`}>
                      {badge.label}
                    </span>
                  </div>
                  <div className="text-xs text-[#1E3A8A] mt-0.5">
                    {r.activity_date} · <strong>{r.hours.toFixed(2)}h</strong>
                  </div>
                  {r.notes && <p className="text-sm text-[#334155] mt-1 whitespace-pre-wrap">{r.notes}</p>}
                  {r.reviewer_notes && (
                    <p className="text-xs text-[#991B1B] mt-1 italic">
                      Reviewer: {r.reviewer_notes}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LogHoursForm({ events, onDone, onCancel }) {
  const [form, setForm] = useState({
    outreach_id: "",
    activity_label: "",
    activity_date: new Date().toISOString().slice(0, 10),
    hours: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = {
        outreach_id: form.outreach_id || null,
        activity_label: !form.outreach_id ? (form.activity_label || null) : null,
        activity_date: form.activity_date,
        hours: parseFloat(form.hours),
        notes: form.notes || null,
      };
      if (!payload.outreach_id && !payload.activity_label) {
        throw new Error("Pick an event or describe the activity");
      }
      await volApi.post("/volunteers/me/hours", payload);
      toast.success("Hours submitted for review");
      onDone();
    } catch (err) {
      setError(err.message || formatVolError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#E5E5E0] rounded-2xl p-5 space-y-4" data-testid="volunteer-hours-form">
      <h3 className="font-display text-lg font-semibold text-[#0F172A]">Log new hours</h3>
      <div>
        <label className="block text-sm font-medium text-[#334155] mb-2">Event (optional)</label>
        <select
          value={form.outreach_id}
          onChange={set("outreach_id")}
          data-testid="volunteer-hours-event"
          className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
        >
          <option value="">— Free-form (no specific event) —</option>
          {events.map((e) => (
            <option key={e.id} value={e.id}>{e.title} ({e.activity_date})</option>
          ))}
        </select>
      </div>
      {!form.outreach_id && (
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Activity description</label>
          <input
            type="text"
            value={form.activity_label}
            onChange={set("activity_label")}
            data-testid="volunteer-hours-label"
            className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
            placeholder="e.g., Delivered kits in Pitt County"
          />
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Date</label>
          <input
            type="date" required
            value={form.activity_date}
            onChange={set("activity_date")}
            data-testid="volunteer-hours-date"
            className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Hours</label>
          <input
            type="number" required min="0.25" max="24" step="0.25"
            value={form.hours}
            onChange={set("hours")}
            data-testid="volunteer-hours-amount"
            className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
            placeholder="2.5"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-[#334155] mb-2">Notes (optional)</label>
        <textarea
          rows={2}
          value={form.notes}
          onChange={set("notes")}
          data-testid="volunteer-hours-notes"
          className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
        />
      </div>
      {error && (
        <div data-testid="volunteer-hours-error" className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={busy}
          data-testid="volunteer-hours-submit"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554] disabled:opacity-60"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          Submit for review
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="volunteer-hours-cancel"
          className="px-5 py-2.5 rounded-full border border-[#E5E5E0] text-[#334155] hover:bg-[#F9F8F6]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
